import { Stack, Text, showToast } from "@certn/cascadia";
import { Divider, Toggle } from "@certn/cascadia-react";
import { useTranslation } from "react-i18next";
import { useClientPortalAPI, useSubmit } from "@certn/api";
import { useCallback } from "react";
import { usePermissions } from "../../../components/permissions/PermissionsProvider";
import { useMfaEnabled } from "@certn/auth";

export function MFAConfig() {
  const { t } = useTranslation();

  const { hasPermission } = usePermissions();

  const { data: me, mutate } = useClientPortalAPI(
    "/api/internal/client-portal/me/"
  );

  const { submit: updateMfaUser, isSubmitting: isUpdatingUserMfa } = useSubmit(
    "/api/internal/client-portal/me/mfa/update/",
    {
      showToastOnError: true,
      onSuccess: async () => {
        showToast(t("Multi-factor authentication setting updated"), {
          type: "success",
        });
      },
    }
  );

  const {
    submit: updateMfaCustomerAccount,
    isSubmitting: isUpdatingCustomerMfa,
  } = useSubmit("/api/internal/client-portal/customer-accounts/mfa/update/", {
    showToastOnError: true,
    onSuccess: async () => {
      showToast(
        t("Multi-factor authentication requirement updated for all users"),
        {
          type: "success",
        }
      );
    },
  });

  const { mfaEnabled, clientAccountMfaEnabled } = useMfaEnabled({ user: me });

  const handleUserMfaToggle = useCallback(
    async (isSelected: boolean) => {
      await updateMfaUser({ use_mfa: isSelected });
      await mutate();
    },
    [mutate, updateMfaUser]
  );

  const handleCustomerMfaToggle = useCallback(
    async (isSelected: boolean) => {
      await updateMfaCustomerAccount({ use_mfa: isSelected });
      await mutate();
    },
    [mutate, updateMfaCustomerAccount]
  );

  return (
    <Stack gap="space-5">
      <Text style="global/heading-large">
        {t("Multi-factor authentication")}
      </Text>
      <Text style="global/heading-medium">{t("My Account")}</Text>
      <Toggle
        // we want to have the toggle in a selected and disabled state when the customerMfa is on
        isSelected={Boolean(mfaEnabled)}
        isDisabled={
          isUpdatingUserMfa || isUpdatingCustomerMfa || clientAccountMfaEnabled
        }
        onChange={handleUserMfaToggle}
        testID="enable-multi-factor-authentication-my-account"
        description={t(
          "Increases security by turning on multi-factor authentication for your account."
        )}
      >
        {t("Enable multi-factor authentication")}
      </Toggle>
      <Text style="global/heading-medium">{t("All Users")}</Text>
      <Toggle
        isSelected={clientAccountMfaEnabled}
        onChange={handleCustomerMfaToggle}
        isDisabled={
          isUpdatingCustomerMfa ||
          !hasPermission({
            action: "update",
            target: "user",
            scope: "all",
          })
        }
        testID="enable-multi-factor-authentication-all-users"
        description={t(
          "Next time any user logs in, they will be prompted to set up multi-factor authentication. Users will be unable to toggle multi-factor authentication themselves."
        )}
      >
        {t("Require multi-factor authentication for all users")}
      </Toggle>
      <Divider />
    </Stack>
  );
}
